import { useQuery } from "@tanstack/react-query";
import { api, FESTIVAL_META, formatCountdown, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import WishlistFeed from "@/components/WishlistFeed";

function totalSlots(f) {
  return f.slots.headliner + f.slots.international + f.slots.danish;
}

export default function Home() {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(x => x+1), 60000); return () => clearInterval(t); }, []);

  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data) });
  const { data: scores } = useQuery({ queryKey: ["my-scores"], queryFn: () => api.get("/scores/mine").then(r => r.data) });
  const { data: preds } = useQuery({ queryKey: ["my-predictions"], queryFn: () => api.get("/predictions/mine").then(r => r.data) });
  const { data: overall } = useQuery({ queryKey: ["lb-overall"], queryFn: () => api.get("/leaderboard/overall").then(r => r.data) });
  const { data: news } = useQuery({ queryKey: ["announcements-home"], queryFn: () => api.get("/announcements?limit=5").then(r => r.data) });

  const cd = festData ? formatCountdown(festData.deadline) : null;
  const overallEntries = Array.isArray(overall) ? overall : (overall?.entries || []);
  const myPos = overallEntries.find(e => e.user_id === user?.user_id)?.position;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <div className="label-eyebrow">Din dashboard</div>
          <h1 className="hero-title text-4xl sm:text-5xl lg:text-6xl mt-2">Hej, {user?.display_name || user?.name?.split(' ')[0]}</h1>
        </div>
        <div className="glass rounded-2xl px-5 py-4 min-w-[240px]" data-testid="home-countdown">
          <div className="label-eyebrow">Deadline</div>
          {cd?.locked ? (
            <div className="mt-1 text-red-400 font-semibold">Predictions er låst</div>
          ) : (
            <div className="mt-1 flex items-baseline gap-3">
              <div className="font-score text-3xl" data-testid="countdown-days">{cd?.days ?? "–"}<span className="text-sm text-zinc-500 ml-1">d</span></div>
              <div className="font-score text-2xl">{cd?.hours ?? "–"}<span className="text-sm text-zinc-500 ml-1">t</span></div>
              <div className="font-score text-2xl">{cd?.minutes ?? "–"}<span className="text-sm text-zinc-500 ml-1">m</span></div>
            </div>
          )}
        </div>
      </div>

      {/* Score row */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-flat p-8 md:col-span-2" data-testid="home-total-score">
          <div className="label-eyebrow">Dine samlede point</div>
          <div className="mt-3 flex items-end gap-4">
            <div className="font-score text-8xl leading-none">{scores?.total ?? 0}</div>
            <div className="pb-2 text-zinc-500 text-sm">/ 426 max</div>
          </div>
        </div>
        <div className="card-flat p-8" data-testid="home-overall-rank">
          <div className="label-eyebrow flex items-center gap-2"><Trophy size={12} /> Placering overall</div>
          <div className="mt-3 font-score text-8xl leading-none">#{myPos ?? "–"}</div>
          <div className="text-zinc-500 text-sm mt-2">{overallEntries.length} spillere</div>
        </div>
      </div>

      {/* Festival cards */}
      <div className="mt-14">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-bold text-3xl">Festivaler</h2>
          <Link to="/forudsigelser" className="text-sm text-zinc-400 hover:text-white" data-testid="home-view-all-predictions">Se alle forudsigelser →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {festData?.festivals.map(f => {
            const meta = FESTIVAL_META[f.id];
            const imgSrc = resolveMediaUrl(f.image_url) || meta.image;
            const p = preds?.[f.id];
            const filled = (p?.headliners?.length || 0) + (p?.international?.length || 0) + (p?.danish?.length || 0);
            const total = totalSlots(f);
            const s = scores?.festivals?.[f.id];
            return (
              <Link
                to={`/festival/${f.id}`}
                key={f.id}
                data-testid={`home-festival-card-${f.id}`}
                className={`card-flat overflow-hidden interactive ${meta.glow} hover:-translate-y-1`}
              >
                <div className="relative aspect-[4/5]">
                  <img src={imgSrc} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.9) 100%)" }} />
                  <div className="absolute top-3 left-3 h-1 w-10" style={{ background: meta.color }} />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <div className="font-display font-bold text-xl leading-tight">{f.name}</div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="label-eyebrow">Point</div>
                        <div className="font-score text-4xl leading-none">{s?.points ?? 0}</div>
                      </div>
                      <div className="text-right">
                        <div className="label-eyebrow">Valgt</div>
                        <div className="font-score text-2xl">{filled}<span className="text-zinc-500 text-sm">/{total}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Top players */}
      <div className="mt-14">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-bold text-3xl">Overall liga</h2>
          <Link to="/leaderboard" className="text-sm text-zinc-400 hover:text-white">Se hele leaderboardet →</Link>
        </div>
        <div className="card-flat overflow-hidden">
          {overall?.locked ? (
            <div className="p-8 text-center" data-testid="home-lb-locked">
              <div className="text-4xl mb-2">🔒</div>
              <div className="font-display font-bold text-lg">Leaderboardet åbner, når deadline er passeret.</div>
              {overall.deadline && <div className="text-zinc-500 text-sm mt-1">Deadline: {new Date(overall.deadline).toLocaleString("da-DK")}</div>}
            </div>
          ) : (overall?.entries || []).slice(0, 5).map((e, i) => (
            <Link to={`/u/${e.user_id}`} key={e.user_id} className={`flex items-center gap-4 px-5 py-4 hover:bg-white/5 ${i < 4 ? "border-b border-white/5" : ""}`} data-testid={`home-lb-row-${i}`}>
              <div className="font-score text-2xl w-10 text-zinc-500">{["🥇","🥈","🥉"][i] || `#${e.position}`}</div>
              {e.picture ? <img src={e.picture} className="w-8 h-8 rounded-full object-cover" alt="" /> : <div className="w-8 h-8 rounded-full bg-zinc-800" />}
              <div className="flex-1 truncate">{e.display_name}</div>
              <div className="font-score text-2xl">{e.total}</div>
            </Link>
          ))}
          {!overall?.locked && (!overall?.entries || overall.entries.length === 0) && <div className="p-6 text-zinc-500 text-center">Ingen spillere endnu.</div>}
        </div>
      </div>

      {/* Latest news */}
      {news && news.length > 0 && (
        <div className="mt-14" data-testid="home-news">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display font-bold text-3xl">Seneste nyt</h2>
            <Link to="/nyheder" className="text-sm text-zinc-400 hover:text-white" data-testid="home-view-all-news">Se alle nyheder →</Link>
          </div>
          <div className="card-flat overflow-hidden">
            {news.slice(0, 5).map((n, i) => {
              const festMeta = FESTIVAL_META[n.festival_id] || {};
              const festName = festMeta.name || n.festival_id;
              const catLabel = n.is_orange_opener ? "Orange opener" : n.is_headliner ? "headliner" : (n.category === "danish" ? "dansk artist" : "artist");
              return (
                <Link
                  to="/nyheder"
                  key={n.announcement_id}
                  data-testid={`home-news-row-${i}`}
                  className={`flex items-center gap-4 px-5 py-4 hover:bg-white/5 ${i < Math.min(news.length, 5) - 1 ? "border-b border-white/5" : ""}`}
                >
                  <div className="h-10 w-1 rounded-full" style={{ background: festMeta.color || "#FFFFFF" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      <span className="text-zinc-400">{festName} annoncerer </span>
                      <span className="text-white">{n.artist_name}</span>
                      <span className="text-zinc-500 text-sm"> · {catLabel}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {new Date(n.created_at).toLocaleDateString("da-DK", { day: "numeric", month: "long" })}
                      {n.predictor_count > 0 && <span> · {n.predictor_count} spiller{n.predictor_count === 1 ? "" : "e"} ramte</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Wishlist */}
      <WishlistFeed />
    </div>
  );
}
